import { useMemo, useState } from 'react';
import styled from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import useWorkflowLogs from 'hooks/useWorkflowLogs';
import { simpleDateAtTimeWithSeconds } from 'lib/date';
import { WorkflowLogEntry } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { Pill, Tone } from './StatusBadge';

// The referral's activity feed (OLIO-2478, Figma "Activity Log"): a chronological
// timeline of who did what, when — newest first, filterable by actor type and by
// person. The chip derives from the actor's kind served by the API.
const KIND_DISPLAY: Record<WorkflowLogEntry['createdBy']['kind'], { label: string; tone: Tone }> = {
  user: { label: 'User', tone: 'info' },
  olio: { label: 'Olio', tone: 'running' },
  integration: { label: 'Integration', tone: 'neutral' },
};

type Option = { value: string; label: string };

const ALL: Option = { value: 'all', label: 'All' };
const TYPE_OPTIONS: Option[] = [ALL, ...Object.entries(KIND_DISPLAY).map(([value, d]) => ({ value, label: d.label }))];

type Props = { workflowId: string; isTerminal?: boolean };

export default function ActivityLog({ workflowId, isTerminal }: Props) {
  const { entries, isLoading, isError } = useWorkflowLogs(workflowId, { isTerminal });
  const [kind, setKind] = useState('all');
  const [actor, setActor] = useState('all');

  // The PEOPLE who appear in this feed, alpha — the design's User filter. Machine
  // actors aren't listed (the Activity Type filter covers them). Keyed by actor id
  // so two people sharing a display name stay distinct filters.
  const actorOptions = useMemo(() => {
    const people = entries.filter((entry) => entry.createdBy.kind === 'user');
    const byId = new Map(people.map((entry) => [entry.createdBy.id, entry.createdBy.name]));
    const options = [...byId].map(([id, name]) => ({ value: id, label: name }));
    return [ALL, ...options.sort((a, b) => a.label.localeCompare(b.label))];
  }, [entries]);

  const visible = entries.filter(
    (entry) => (kind === 'all' || entry.createdBy.kind === kind) && (actor === 'all' || entry.createdBy.id === actor)
  );

  if (isLoading)
    return (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  if (isError) return <Muted>We couldn’t load this referral’s activity.</Muted>;

  return (
    <div>
      <Filters>
        <FilterSelect data-testid='activity-type-filter'>
          <Select<Option>
            options={TYPE_OPTIONS}
            value={TYPE_OPTIONS.find((option) => option.value === kind) ?? ALL}
            onChange={(option) => setKind(option?.value ?? 'all')}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            formatOptionLabel={(option, meta) =>
              meta?.context === 'value' ? `Activity Type: ${option.label}` : option.label
            }
          />
        </FilterSelect>
        <FilterSelect data-testid='activity-user-filter'>
          <Select<Option>
            options={actorOptions}
            value={actorOptions.find((option) => option.value === actor) ?? ALL}
            onChange={(option) => setActor(option?.value ?? 'all')}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            formatOptionLabel={(option, meta) => (meta?.context === 'value' ? `User: ${option.label}` : option.label)}
          />
        </FilterSelect>
      </Filters>

      <Timeline>
        {visible.map((entry, index) => {
          const chip = KIND_DISPLAY[entry.createdBy.kind] ?? KIND_DISPLAY.user;
          return (
            <Row key={entry.id} data-testid='activity-entry'>
              <Marker>
                <Ring />
                {index < visible.length - 1 && <Connector />}
              </Marker>
              <RowMain>
                <RowHeadline>
                  <Pill $tone={chip.tone}>{chip.label}</Pill>
                  <Summary>{entry.summary}</Summary>
                </RowHeadline>
                <Meta>
                  {/* Machine entries wear their chip; a "by" line only makes sense for people. */}
                  {simpleDateAtTimeWithSeconds(entry.createdAt)}
                  {entry.createdBy.kind === 'user' && ` by ${entry.createdBy.name}`}
                </Meta>
              </RowMain>
            </Row>
          );
        })}
        {visible.length === 0 && <Muted data-testid='activity-empty'>No activity to show.</Muted>}
      </Timeline>
    </div>
  );
}

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const Filters = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
`;

const FilterSelect = styled.div`
  min-width: 200px;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  gap: 14px;
`;

const Marker = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 16px;
  flex-shrink: 0;
  padding-top: 3px;
`;

const Ring = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1.5px solid ${colors.primaryBlue};
  flex-shrink: 0;
`;

const Connector = styled.span`
  width: 1.5px;
  flex: 1;
  min-height: 18px;
  margin: 4px 0;
  background: ${colors.black15};
`;

const RowMain = styled.div`
  padding-bottom: 22px;
  min-width: 0;
`;

const RowHeadline = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Summary = styled(BodySmall)`
  color: ${colors.black};
  font-size: 14px;
`;

const Meta = styled(BodySmall)`
  display: block;
  margin-top: 4px;
  color: ${colors.black50};
  font-size: 12px;
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;
